import React, { memo, useCallback, useMemo, useRef } from 'react';
import { Table, Tag, Tooltip, Button, Popconfirm } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { DeleteOutlined } from '@ant-design/icons';
import { Log, LogPagination } from '../../../Modules/Log/Model/Log';
import { extractMainKey, actionMeta } from './helpers';
import dayjs from 'dayjs';
import { getAccountIdById } from '../../../Utils/getAccountNameById';
import { useAppDispatch, useAppSelector } from '../../../Modules/Presentation/hooks';
import { selectAccounts } from '../../../Modules/Account/Presentation/selectors';
import useOnce from '../../../Utils/hook/useOnce';
import { fetchAccounts } from '../../../Modules/Account/Presentation/actions';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import { extractChangedFieldsFromLogDescription } from '../../../Utils/logChangedFields';
import { canDeleteLogs } from '../../../Utils/userPermissions';

interface Props {
  data: Log[];
  loading?: boolean;
  selected: React.Key[];
  onSelectChange: (selectedRowKeys: React.Key[]) => void;
  onDelete: (logId: number) => void;
  onEdit?: (log: Log) => void;
  pagination?: LogPagination | null;
  onPageChange?: (page: number, pageSize?: number) => void;
}

const fieldLabel: Record<string, string> = {
  main_keyword: '메인 키워드',
  word_keyword: '작업 키워드',
  mid_single: '단일 MID',
  mid_compare: '가격비교 MID',
  product_url: '상품 URL',
  compare_url: '가격비교 URL',
  memo: '메모',
  start_date: '시작일',
  end_date: '종료일',
  status: '상태',
  user: '아이디',
  manager: '생성자',
};

function renderDetailsTooltip(
  description: string,
  slotSnapshot?: Record<string, unknown> | null,
) {
  const changed = extractChangedFieldsFromLogDescription(description, slotSnapshot);
  const keys = Object.keys(changed);
  if (keys.length === 0) return undefined;
  return (
    <div style={{ maxWidth: 360, color: '#fff' }}>
      {keys.map(k => {
        const before = changed[k]?.before ?? '-';
        const after = changed[k]?.after ?? '-';
        const label = fieldLabel[k] || k;
        return (
          <div key={k} style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 700, color: '#fff' }}>{label}</div>
            <div style={{ fontSize: 12, color: '#ddd' }}>before: {String(before)}</div>
            <div style={{ fontSize: 12, color: '#fff' }}>after: {String(after)}</div>
          </div>
        );
      })}
    </div>
  );
}

const LogTable: React.FC<Props> = ({
  data,
  loading = false,
  selected,
  onSelectChange,
  onDelete,
  pagination,
  onPageChange,
}) => {

  const dispatch = useAppDispatch();
  const accountList = useAppSelector(selectAccounts);
  const currentUser = useAppSelector(selectUser);

  useOnce(() => {
    if (accountList.length === 0) {
      dispatch(fetchAccounts());
    }
  })

  const isAdmin = currentUser?.grade === 'ADMIN';
  const canDeleteLog = canDeleteLogs(currentUser);

  // description → tooltip 렌더는 JSON 파싱이 포함되어 비싸서 캐시
  const detailsCacheRef = useRef<Map<string, React.ReactNode | null>>(new Map());
  const getDetailsTooltipCached = useCallback(
    (text: string, slotSnapshot?: Record<string, unknown> | null) => {
      if (!text) return undefined;
      const snapKey = slotSnapshot ? JSON.stringify(slotSnapshot) : '';
      const cacheKey = `${text}\n${snapKey}`;
      const cached = detailsCacheRef.current.get(cacheKey);
      if (cached !== undefined) return cached === null ? undefined : cached;
      const computed = renderDetailsTooltip(text, slotSnapshot) ?? null;
      detailsCacheRef.current.set(cacheKey, computed);
      return computed === null ? undefined : computed;
    },
    [],
  );

  const columns: ColumnsType<Log> = useMemo(() => {
    const base: ColumnsType<Log> = [
      {
        title: '행위', dataIndex: 'action', width: 140,
        render: (full: string) => {
          const key = extractMainKey(full);
          const meta = actionMeta[key] ?? { color: 'default', label: full };
          return <Tag color={meta.color}>{meta.label}</Tag>;
        },
      },
      { title: '슬롯 넘버', dataIndex: 'slotId', width: 90 },
      {
        title: '아이디',
        dataIndex: 'sellerId',
        width: 200,
        render: (_: number | null, record) =>
          record.sellerUsername
          ?? (record.sellerId ? getAccountIdById(accountList, record.sellerId, true, currentUser) : '-'),
      },
      {
        title: '상위 계정 아이디',
        dataIndex: 'sellerParentId',
        width: 200,
        render: (_: number | null, record) =>
          record.sellerParentUsername
          ?? (record.sellerParentId ? getAccountIdById(accountList, record.sellerParentId, true, currentUser) : '-'),
      },
      {
        title: '시작일',
        dataIndex: 'start_date',
        width: 120,
        render: (date: string | null) => date ? dayjs(date).format('YYYY-MM-DD') : '-'
      },
      {
        title: '종료일',
        dataIndex: 'end_date',
        width: 120,
        render: (date: string | null) => date ? dayjs(date).format('YYYY-MM-DD') : '-'
      },
      {
        title: '설명', dataIndex: 'description', width: 260,
        render: (text: string, record: Log) => {
          const tip = getDetailsTooltipCached(text, record.slot_snapshot ?? undefined);
          return tip ? (
            <Tooltip title={tip} trigger="click" overlayInnerStyle={{ textAlign: 'left', backgroundColor: '#1f1f1f', color: '#fff' }}>
              <span style={{ cursor: 'help' }}>{text.split('| details=')[0].trim()}</span>
            </Tooltip>
          ) : (
            <span>{text}</span>
          );
        }
      },
      { title: '기간', dataIndex: 'total_days', width: 80 },
      { title: '발주일수', dataIndex: 'final_days', width: 120 },
      {
        title: '일시',
        dataIndex: 'created_at',
        width: 180,
        render: (v: string) =>
          dayjs(v).isValid()
            ? dayjs(v).format('YYYY-MM-DD HH:mm:ss')
            : '-',
      },
    ];

    if (!isAdmin) return base;

    const columns = [...base];

    columns.push({
      title: '작업자',
      dataIndex: 'actor_username',
      width: 160,
      render: (v: string | null | undefined) => v ?? '-',
    });

    if (canDeleteLog) {
      columns.push({
        title: '작업',
        width: 80,
        render: (_, record) => (
          <Popconfirm
            title="로그를 삭제하시겠습니까?"
            description="삭제된 로그는 복구할 수 없습니다."
            onConfirm={() => onDelete(record.logId)}
            okText="삭제"
            cancelText="취소"
            okType="danger"
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        ),
      });
    }

    return columns;
  }, [accountList, currentUser, getDetailsTooltipCached, isAdmin, canDeleteLog, onDelete]);

  const rowSelection = useMemo(() => (
    canDeleteLog ? {
      selectedRowKeys: selected,
      onChange: onSelectChange,
      preserveSelectedRowKeys: true,
    } : undefined
  ), [canDeleteLog, selected, onSelectChange]);

  const handleTableChange = useCallback((pagination: any) => {
    if (pagination?.current !== undefined) {
      onPageChange?.(pagination.current, pagination.pageSize || 20);
    }
  }, [onPageChange]);

  const tablePagination = pagination ? {
    current: pagination.current_page || 1,
    pageSize: pagination.page_size || 20,
    total: pagination.count || 0,
    position: ['bottomCenter' as const],
    showSizeChanger: true,
    pageSizeOptions: ['20', '50', '100', '200'],
    showQuickJumper: false,
    showTotal: (total: number, range: [number, number]) =>
      `${range[0]}-${range[1]} / ${total.toLocaleString()}개 로그`,
    showLessItems: true,
    responsive: true,
  } : false;

  return (
    <Table
      rowKey="logId"
      dataSource={data}
      columns={columns}
      loading={loading}
      rowSelection={rowSelection}
      pagination={tablePagination}
      scroll={{ x: 'max-content' }}
      locale={{ emptyText: '조회된 로그가 없습니다.' }}
      onChange={handleTableChange}
    />
  );
};

export default memo(LogTable);
